"use client";

import { FormattedMessage, useIntl } from "react-intl";
import {
  useCallback,
  useEffect,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { Radio, Select, Space, Tooltip } from "antd";
import { CheckboxGroupProps } from "antd/es/checkbox";
import { DefaultOptionType } from "antd/es/select";
import { ICoin } from "@/data/api/main-data";
import { useWindowSize } from "@/hooks/window-size";
import {
  COIN_SELECTOR_MOBILE_PRESET_LOGGED_MAIN,
  ICoinSelectorMobilePreset,
} from "@/components/coin-selector/mobile-presets";
import {
  getBaseCoinName,
  getDefaultAlgorithmFromMap,
  getDefaultCoinByAlgorithmFromMap,
} from "@/app/shared/coin-utils";

import IconBTC from "@/public/icons/coins/bitcoin-logo.svg";
import IconBCHN from "@/public/icons/coins/bitcoin-cash.svg";
import IconDGB from "@/public/icons/coins/digibyte.svg";
import IconDOGE from "@/public/icons/coins/dogecoin.svg";
import IconXEC from "@/public/icons/coins/ecash.svg";
import IconHTR from "@/public/icons/coins/icon.svg";
import IconLTC from "@/public/icons/coins/litecoin.svg";
import IconXPM from "@/public/icons/coins/primecoin.svg";
import IconEmpty from "@/public/icons/coins/1px.svg";

export function CoinSelectorWithMap({
  coinsMap,
  coinsInfo,
  callback,
  selectedAlgorithm,
  selectedCoin,
  forceMobileSingleLine = false,
  mobilePreset = COIN_SELECTOR_MOBILE_PRESET_LOGGED_MAIN,
}: {
  coinsMap: Map<string, string[]>;
  coinsInfo?: Map<string, ICoin> | undefined;
  callback: (alg: string, coin: string) => void;
  selectedAlgorithm?: string;
  selectedCoin?: string;
  forceMobileSingleLine?: boolean;
  mobilePreset?: ICoinSelectorMobilePreset;
}) {
  const intl = useIntl();
  const windowSize = useWindowSize();
  const isMobile = windowSize.width <= 768;
  const mobileSingleLine = isMobile && forceMobileSingleLine;
  const mobileLabelFontSize = `${mobilePreset.labelFontSizeRem}rem`;
  const mobileSelectFontSize =
    mobilePreset.selectFontSizeRem != undefined
      ? `${mobilePreset.selectFontSizeRem}rem`
      : undefined;
  const defAlg = useMemo(
    () => getDefaultAlgorithmFromMap(coinsMap),
    [coinsMap]
  );
  const [coin, setCoin] = useState<string | undefined>(() => {
    if (selectedAlgorithm && selectedCoin) return selectedCoin;
    if (selectedAlgorithm) {
      return getDefaultCoinByAlgorithmFromMap(coinsMap, selectedAlgorithm);
    }
    return getDefaultCoinByAlgorithmFromMap(coinsMap, defAlg);
  });
  const [algorithm, setAlgorithm] = useState<string | undefined>(() => {
    if (selectedAlgorithm && coinsMap.has(selectedAlgorithm)) {
      return selectedAlgorithm;
    }
    return getDefaultAlgorithmFromMap(coinsMap);
  });
  const [desktopUseCoinSelect, setDesktopUseCoinSelect] = useState(false);
  const selectorLayoutRef = useRef<HTMLDivElement | null>(null);
  const algorithmControlRef = useRef<HTMLSpanElement | null>(null);
  const desktopRadioMeasureRef = useRef<HTMLDivElement | null>(null);
  const desktopRadioVisibleRef = useRef<HTMLSpanElement | null>(null);

  useEffect(() => {
    if (selectedAlgorithm == undefined && selectedCoin == undefined) return;

    if (selectedAlgorithm != undefined && coinsMap.has(selectedAlgorithm)) {
      if (algorithm != selectedAlgorithm) {
        setAlgorithm(selectedAlgorithm);
      }

      const algorithmCoins = coinsMap.get(selectedAlgorithm) ?? [];
      const nextCoin =
        selectedCoin != undefined && algorithmCoins.indexOf(selectedCoin) != -1
          ? selectedCoin
          : algorithmCoins[algorithmCoins.length - 1];

      if (nextCoin != undefined && coin != nextCoin) {
        setCoin(nextCoin);
      }

      return;
    }

    if (selectedCoin != undefined) {
      for (const [alg, coins] of coinsMap.entries()) {
        if (coins.indexOf(selectedCoin) != -1) {
          if (algorithm != alg) setAlgorithm(alg);
          if (coin != selectedCoin) setCoin(selectedCoin);
          return;
        }
      }
    }
  }, [coinsMap, selectedAlgorithm, selectedCoin, algorithm, coin]);

  const selectionOptions = useMemo(
    () => provideAlgorithmOptions(coinsMap),
    [coinsMap]
  );

  const coinsSelectionOptions = useMemo(
    () => provideCoinsSelectionOptions(coinsMap, algorithm),
    [coinsMap, algorithm]
  );

  const handleAlgorithmSelected = (alg: string): void => {
    const nextCoin = getDefaultCoinByAlgorithmFromMap(coinsMap, alg);
    if (!nextCoin) return;
    setAlgorithm(alg);
    setCoin(nextCoin);
    callback(alg, nextCoin);
  };

  const handleCoinSelected = (coin: string): void => {
    setCoin(coin);
    callback(algorithm!, coin);
  };

  const getOptions = useCallback((): CheckboxGroupProps<string>["options"] => {
    const coins = coinsMap.get(algorithm!);
    const result: CheckboxGroupProps<string>["options"] = [];

    coins?.map((coin) => {
      result.push({
        label: (
          <Tooltip
            placement="bottom"
            style={{ marginTop: "0.5em" }}
            title={
              provideCoinTooltipTitle(
                coin,
                algorithm,
                coinsInfo
              )
            }
          >
            {provideCoinLabel(coin)}
          </Tooltip>
        ),
        value: coin,
      });
    });
    return result;
  }, [algorithm, coinsInfo, coinsMap]);

  const radioOptions = useMemo(() => getOptions(), [getOptions]);
  const showDesktopRadio = algorithm != undefined && !isMobile && !desktopUseCoinSelect;
  const showCoinSelect = algorithm != undefined && (isMobile || desktopUseCoinSelect);

  useLayoutEffect(() => {
    if (isMobile || algorithm == undefined) {
      setDesktopUseCoinSelect(false);
      return;
    }

    const recomputeDesktopMode = () => {
      const layoutWidth = selectorLayoutRef.current?.clientWidth ?? 0;
      const algorithmWidth = algorithmControlRef.current?.offsetWidth ?? 0;
      const radioWidth =
        desktopRadioMeasureRef.current?.getBoundingClientRect().width ?? 0;

      if (layoutWidth <= 0 || algorithmWidth <= 0 || radioWidth <= 0) return;

      const visibleHost = desktopRadioVisibleRef.current;
      if (visibleHost != undefined) {
        const buttonWrappers = visibleHost.querySelectorAll<HTMLElement>(
          ".ant-radio-button-wrapper"
        );
        if (buttonWrappers.length > 1) {
          const firstTop = buttonWrappers[0].offsetTop;
          for (let i = 1; i < buttonWrappers.length; i++) {
            if (Math.abs(buttonWrappers[i].offsetTop - firstTop) > 1) {
              setDesktopUseCoinSelect(true);
              return;
            }
          }
        }
      }

      // Keep a margin to avoid one-pixel oscillations near the threshold.
      const requiredWidth = algorithmWidth + radioWidth + 16;
      setDesktopUseCoinSelect((prev) => {
        if (prev) {
          return requiredWidth > layoutWidth - 8;
        }
        return requiredWidth > layoutWidth;
      });
    };

    recomputeDesktopMode();

    const observer =
      typeof ResizeObserver == "undefined"
        ? undefined
        : new ResizeObserver(() => {
            recomputeDesktopMode();
          });

    if (selectorLayoutRef.current && observer) {
      observer.observe(selectorLayoutRef.current);
    }
    if (algorithmControlRef.current && observer) {
      observer.observe(algorithmControlRef.current);
    }
    if (desktopRadioMeasureRef.current && observer) {
      observer.observe(desktopRadioMeasureRef.current);
    }
    if (desktopRadioVisibleRef.current && observer) {
      observer.observe(desktopRadioVisibleRef.current);
    }

    window.addEventListener("resize", recomputeDesktopMode);

    return () => {
      observer?.disconnect();
      window.removeEventListener("resize", recomputeDesktopMode);
    };
  }, [algorithm, isMobile, radioOptions]);

  return (
    <div ref={selectorLayoutRef} style={{ width: "100%", minWidth: 0 }}>
      <Space
        wrap={isMobile ? !mobileSingleLine : false}
        size={isMobile ? "small" : "middle"}
        className="notranslate"
        translate="no"
        style={
          mobileSingleLine
            ? { width: "100%", gap: mobilePreset.singleLineGapPx }
            : undefined
        }
      >
        <span
          ref={algorithmControlRef}
          style={{
            display: "inline-flex",
            alignItems: "center",
            gap: mobilePreset.controlGapPx,
            whiteSpace: "nowrap",
            minWidth: 0,
          }}
        >
          <span
            style={{
              fontSize: isMobile ? mobileLabelFontSize : undefined,
            }}
          >
            <FormattedMessage id="title-algorithm-short" />
          </span>
          <Select
            size={isMobile ? "small" : "middle"}
            style={
              mobileSingleLine
                ? {
                    width: mobilePreset.algorithmWidthPx,
                    fontSize: isMobile ? mobileSelectFontSize : undefined,
                  }
                : {
                    minWidth: isMobile
                      ? `${mobilePreset.algorithmWidthPx}px`
                      : "120px",
                    fontSize: isMobile ? mobileSelectFontSize : undefined,
                  }
            }
            value={
              algorithm != undefined && algorithm != null
                ? algorithm
                : defAlg != undefined && defAlg != null
                ? defAlg
                  : getDefaultAlgorithmFromMap(coinsMap)
            }
            placeholder={intl.messages["title-algorithm"] as string}
            onChange={handleAlgorithmSelected}
            options={selectionOptions}
          />
        </span>

        {showDesktopRadio && (
          <span
            ref={desktopRadioVisibleRef}
            style={{ display: "inline-flex", whiteSpace: "nowrap", minWidth: 0 }}
          >
            <Radio.Group
              key={`r-buttons-${algorithm}`}
              onChange={(e) => handleCoinSelected(e.target.value)}
              options={radioOptions}
              optionType="button"
              block={false}
              value={coin}
            />
          </span>
        )}

        {showCoinSelect && (
          <span
            style={{
              display: "inline-flex",
              alignItems: "center",
              gap: mobilePreset.controlGapPx,
              whiteSpace: "nowrap",
              minWidth: 0,
            }}
          >
            <span
              style={{
                fontSize: isMobile ? mobileLabelFontSize : undefined,
              }}
            >
              <FormattedMessage id="title-coin-short" />
            </span>
            <Select
              size={isMobile ? "small" : "middle"}
              style={
                mobileSingleLine
                  ? {
                      width: mobilePreset.coinWidthPx,
                      fontSize: isMobile ? mobileSelectFontSize : undefined,
                    }
                  : {
                      minWidth: isMobile ? `${mobilePreset.coinWidthPx}px` : "140px",
                      fontSize: isMobile ? mobileSelectFontSize : undefined,
                    }
              }
              value={coin}
              placeholder={intl.messages["title-coin"] as string}
              onChange={handleCoinSelected}
              options={coinsSelectionOptions}
            />
          </span>
        )}
      </Space>

      {!isMobile && algorithm != undefined && (
        <div
          ref={desktopRadioMeasureRef}
          aria-hidden
          style={{
            position: "absolute",
            left: -100000,
            top: 0,
            visibility: "hidden",
            pointerEvents: "none",
            whiteSpace: "nowrap",
            display: "inline-flex",
          }}
        >
          <Radio.Group
            key={`r-buttons-measure-${algorithm}`}
            options={radioOptions}
            optionType="button"
            block={false}
            value={coin}
          />
        </div>
      )}
    </div>
  );
}

// function provideAlgorithmOptions(
//   map: Map<string, string[]>
// ): DefaultOptionType[] {
//   if (map == undefined) return [];

//   const result: DefaultOptionType[] = [];
//   map.keys().forEach((key) => {
//     result.push({
//       label: key,
//       value: key,
//     });
//   });
//   return result;
// }

function provideAlgorithmOptions(
  map: Map<string, string[]>
): DefaultOptionType[] {
  if (!map) return [];

  const result: DefaultOptionType[] = [];

  for (const key of map.keys()) {
    result.push({
      label: key,
      value: key,
    });
  }

  return result;
}

// function provideCoinsSelectionOptions(
//   map: Map<string, string[]>,
//   selectedAlgorithm: string | undefined
// ): DefaultOptionType[] {
//   if (map == undefined || selectedAlgorithm == undefined) return [];
// 
//   const result: DefaultOptionType[] = [];
// 
//   map
//     .get(selectedAlgorithm)
//     ?.values()
//     .forEach((key) => {
//       result.push({
//         label: provideCoinLabel(key),
//         value: key,
//       });
//     });
// 
//   return result;
// }

function provideCoinsSelectionOptions(
  map: Map<string, string[]>,
  selectedAlgorithm: string | undefined
): DefaultOptionType[] {
  if (!map || !selectedAlgorithm) return [];

  const result: DefaultOptionType[] = [];

  const coins = map.get(selectedAlgorithm);
  if (coins) {
    for (const key of coins) {
      result.push({
        label: provideCoinLabel(key),
        value: key,
      });
    }
  }

  return result;
}

const coinLabelRootStyle = {
  display: "inline-flex",
  alignItems: "center",
  gap: "2px",
  minWidth: 0,
  whiteSpace: "nowrap",
} as const;

const coinLabelIconStyle = {
  display: "inline-flex",
  alignItems: "center",
  justifyContent: "center",
  flex: "0 0 auto",
  flexShrink: 0,
  lineHeight: 1,
  position: "relative",
  zIndex: 1,
} as const;

const coinLabelTextStyle = {
  minWidth: 0,
  overflow: "hidden",
  textOverflow: "ellipsis",
  whiteSpace: "nowrap",
  position: "relative",
  zIndex: 0,
} as const;

function provideCoinLabelWithIcon(
  icon: React.ReactNode,
  text: string
): React.ReactNode {
  return (
    <span style={coinLabelRootStyle}>
      <span style={coinLabelIconStyle}>{icon}</span>
      <span style={coinLabelTextStyle}>{text}</span>
    </span>
  );
}

export function provideCoinLabel(coin: string): React.ReactNode | string {
  if (coin == "BTC") {
    return provideCoinLabelWithIcon(<IconBTC />, `\u{00A0}${coin}`);
  } else if (coin == "BCHN") {
    return provideCoinLabelWithIcon(<IconBCHN />, `\u{00A0}${coin}`);
  } else if (coin.includes("DGB")) {
    return provideCoinLabelWithIcon(
      <IconDGB />,
      `\u{00A0}${getSplittedCoin(coin)}`
    );
  } else if (coin == "HTR") {
    return provideCoinLabelWithIcon(<IconHTR />, `\u{00A0}${coin}`);
  } else if (coin == "XEC") {
    return provideCoinLabelWithIcon(<IconXEC />, `\u{00A0}${coin}`);
  } else if (coin == "DOGE") {
    return provideCoinLabelWithIcon(<IconDOGE />, `\u{00A0}${coin}`);
  } else if (coin == "LTC") {
    return provideCoinLabelWithIcon(<IconLTC />, `\u{00A0}${coin}`);
  } else if (coin == "XPM") {
    return provideCoinLabelWithIcon(<IconXPM />, `\u{00A0}${coin}`);
  }

  return provideCoinLabelWithIcon(<IconEmpty />, coin);
}

function getSplittedCoin(coin: string): string {
  return getBaseCoinName(coin);
}

function provideCoinTooltipTitle(
  coin: string,
  algorithm: string | undefined,
  coinsInfo: Map<string, ICoin> | undefined
): string {
  const fullName = coinsInfo?.get(coin)?.fullName ?? coin;
  const baseCoin = getSplittedCoin(coin).toUpperCase();

  if (baseCoin != "DGB" || !algorithm) {
    return fullName;
  }

  const algNormalized = algorithm.toLowerCase();
  if (fullName.toLowerCase().includes(algNormalized)) {
    return fullName;
  }

  return `${fullName} (${algorithm})`;
}
